import React, { useState, useEffect } from 'react';
import { Check, X, Save, AlertCircle, Loader2, Shield, Users, FileText, BarChart3, Download, Settings } from 'lucide-react';
import type { UserRole, Permission, RolePermission } from '../types/database';

interface RolePermissionManagerProps {
  onSave?: (role: UserRole, permissions: Permission[]) => void;
  isLoading?: boolean;
}

// Define all available permissions with their descriptions
const PERMISSION_DESCRIPTIONS: Record<Permission, { label: string; description: string; icon: React.ReactNode }> = {
  read_financial_data: {
    label: 'Read Financial Data',
    description: 'View financial statements, metrics, and analysis',
    icon: <BarChart3 className="w-4 h-4" />
  },
  write_financial_data: {
    label: 'Write Financial Data',
    description: 'Create, edit, and update financial data',
    icon: <BarChart3 className="w-4 h-4" />
  },
  read_deal_scenarios: {
    label: 'Read Deal Scenarios',
    description: 'View deal structure analysis and scenarios',
    icon: <FileText className="w-4 h-4" />
  },
  write_deal_scenarios: {
    label: 'Write Deal Scenarios',
    description: 'Create and modify deal scenarios',
    icon: <FileText className="w-4 h-4" />
  },
  read_loi_documents: {
    label: 'Read LOI Documents',
    description: 'View Letters of Intent and related documents',
    icon: <FileText className="w-4 h-4" />
  },
  write_loi_documents: {
    label: 'Write LOI Documents',
    description: 'Create, edit, and manage LOI documents',
    icon: <FileText className="w-4 h-4" />
  },
  manage_users: {
    label: 'Manage Users',
    description: 'Add, edit, and manage user accounts and roles',
    icon: <Users className="w-4 h-4" />
  },
  admin_access: {
    label: 'Admin Access',
    description: 'Full administrative access to all features',
    icon: <Shield className="w-4 h-4" />
  },
  export_data: {
    label: 'Export Data',
    description: 'Export financial data and reports',
    icon: <Download className="w-4 h-4" />
  },
  view_analytics: {
    label: 'View Analytics',
    description: 'Access to analytics dashboard and insights',
    icon: <BarChart3 className="w-4 h-4" />
  }
};

// Default permissions for each role
const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  Admin: [
    'read_financial_data',
    'write_financial_data',
    'read_deal_scenarios',
    'write_deal_scenarios',
    'read_loi_documents',
    'write_loi_documents',
    'manage_users',
    'admin_access',
    'export_data',
    'view_analytics'
  ],
  Lender: [
    'read_financial_data',
    'read_deal_scenarios',
    'read_loi_documents',
    'view_analytics',
    'export_data'
  ],
  Seller: [
    'read_financial_data',
    'read_deal_scenarios',
    'read_loi_documents',
    'view_analytics'
  ],
  Viewer: [
    'read_financial_data',
    'view_analytics'
  ]
};

export function RolePermissionManager({ onSave, isLoading = false }: RolePermissionManagerProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>('Lender');
  const [rolePermissions, setRolePermissions] = useState<Record<UserRole, Permission[]>>(DEFAULT_ROLE_PERMISSIONS);
  const [hasChanges, setHasChanges] = useState(false);
  const [message, setMessage] = useState('');

  // Initialize with default permissions
  useEffect(() => {
    setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
  }, []);

  const handlePermissionToggle = (permission: Permission) => {
    const currentPermissions = rolePermissions[selectedRole];
    const isCurrentlyGranted = currentPermissions.includes(permission);
    
    let newPermissions: Permission[];
    if (isCurrentlyGranted) {
      newPermissions = currentPermissions.filter(p => p !== permission);
    } else {
      newPermissions = [...currentPermissions, permission];
    }
    
    setRolePermissions(prev => ({
      ...prev,
      [selectedRole]: newPermissions
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(selectedRole, rolePermissions[selectedRole]);
      setHasChanges(false);
      setMessage(`Permissions updated for ${selectedRole} role`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleReset = () => {
    setRolePermissions(prev => ({
      ...prev,
      [selectedRole]: DEFAULT_ROLE_PERMISSIONS[selectedRole]
    }));
    setHasChanges(false);
    setMessage(`Reset to default permissions for ${selectedRole}`);
    setTimeout(() => setMessage(''), 3000);
  };

  const getRoleColor = (role: UserRole): string => {
    const colors: Record<UserRole, string> = {
      Admin: 'bg-red-50 border-red-200 text-red-900',
      Lender: 'bg-blue-50 border-blue-200 text-blue-900',
      Seller: 'bg-green-50 border-green-200 text-green-900',
      Viewer: 'bg-gray-50 border-gray-200 text-gray-900'
    };
    return colors[role] ?? 'bg-gray-50 border-gray-200 text-gray-900';
  };

  const getRoleIcon = (role: UserRole): React.ReactNode => {
    const icons: Record<UserRole, React.ReactNode> = {
      Admin: <Shield className="w-5 h-5" />,
      Lender: <Users className="w-5 h-5" />,
      Seller: <FileText className="w-5 h-5" />,
      Viewer: <BarChart3 className="w-5 h-5" />
    };
    return icons[role];
  };

  const currentPermissions = rolePermissions[selectedRole];
  const allPermissions = Object.keys(PERMISSION_DESCRIPTIONS) as Permission[];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-purple-600" />
          Role Permission Customization
        </h3>
        {hasChanges && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        )}
      </div>

      {message && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600" />
            <p className="text-green-800 text-sm">{message}</p>
          </div>
        </div>
      )}

      {/* Role Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Role to Customize
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(Object.keys(DEFAULT_ROLE_PERMISSIONS) as UserRole[]).map((role) => (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              className={`p-3 rounded-lg border-2 transition-all ${
                selectedRole === role
                  ? `${getRoleColor(role)} border-opacity-100`
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                {getRoleIcon(role)}
                <span className="font-medium">{role}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Current Role Info */}
      <div className={`mb-6 p-4 rounded-lg border ${getRoleColor(selectedRole)}`}>
        <div className="flex items-center gap-2 mb-2">
          {getRoleIcon(selectedRole)}
          <h4 className="font-semibold">{selectedRole} Role Permissions</h4>
        </div>
        <p className="text-sm opacity-80">
          Customize the permissions for the {selectedRole} role. Check the boxes below to grant access to specific features.
        </p>
      </div>

      {/* Permission Checkboxes */}
      <div className="space-y-4">
        <h4 className="font-medium text-gray-900 mb-3">Available Permissions</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {allPermissions.map((permission) => {
            const isGranted = currentPermissions.includes(permission);
            const permissionInfo = PERMISSION_DESCRIPTIONS[permission];
            
            return (
              <div
                key={permission}
                className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  isGranted
                    ? 'bg-green-50 border-green-200'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handlePermissionToggle(permission)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isGranted
                        ? 'bg-green-600 border-green-600'
                        : 'border-gray-300'
                    }`}>
                      {isGranted && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {permissionInfo.icon}
                      <span className="font-medium text-gray-900">
                        {permissionInfo.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {permissionInfo.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Current {selectedRole} Permissions Summary</h4>
        <div className="flex flex-wrap gap-2">
          {currentPermissions.length > 0 ? (
            currentPermissions.map((permission) => (
              <span
                key={permission}
                className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
              >
                {PERMISSION_DESCRIPTIONS[permission].label}
              </span>
            ))
          ) : (
            <span className="text-gray-500 text-sm">No permissions granted</span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Total permissions: {currentPermissions.length} of {allPermissions.length}
        </p>
      </div>
    </div>
  );
}
